import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Pusher, { type Channel } from "pusher-js";
import { useParams } from "next/navigation";

// Define a type for the Pusher context
export interface PusherContextType {
  pusher: Pusher | null;
  workplaceChannel: Channel | null;
  subscribeToEvents: (subscriptions: ChannelSubscription[]) => string | null;
  unsubscribeById: (subscriptionId: string) => void;
}

// Create a context for the Pusher instance
const PusherContext = createContext<PusherContextType>({
  pusher: null,
  workplaceChannel: null,
  subscribeToEvents: () => {
    // eslint-disable-next-line no-console
    console.warn("PusherProvider not mounted; subscribeToEvents is a no-op");
    return null;
  },
  unsubscribeById: (subscriptionId: string) => {
    // eslint-disable-next-line no-console
    console.warn(
      "PusherProvider not mounted; unsubscribeById is a no-op",
      subscriptionId,
    );
  },
});

// Custom hook to use Pusher context
export const usePusher = () => useContext(PusherContext);

export type ChannelEventHandler = (data: unknown) => void;
export interface ChannelSubscription {
  channel: string | ((ctx: { subdomain?: string | null }) => string);
  events: Record<string, ChannelEventHandler>;
}

// Props interface for PusherProvider
interface PusherProviderProps {
  children: React.ReactNode;
}

// Functional component for PusherProvider
const PusherProvider: React.FC<PusherProviderProps> = ({ children }) => {
  const [pusher, setPusher] = useState<Pusher | null>(null);
  const [workplaceChannel, setWorkplaceChannel] = useState<Channel | null>(
    null,
  );
  const params = useParams<{ subdomain: string }>();
  const subdomain = (params?.subdomain as string | undefined) || null;

  // Track shared channel subscriptions by name for ref counting
  const channelsRef = useRef<Map<string, { channel: Channel; refs: number }>>(
    new Map(),
  );
  // Track active subscription groups for unsubscription
  const registrationsRef = useRef<
    Map<
      string,
      {
        channelNames: string[];
        bindings: Array<{
          channelName: string;
          eventName: string;
          handler: ChannelEventHandler;
        }>;
      }
    >
  >(new Map());

  useEffect(() => {
    if (!workplaceChannel && !pusher && params.subdomain) {
      const pusherInstance = new Pusher(
        process.env.NEXT_PUBLIC_PUSHER_KEY as string,
        {
          cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER as string,
          authEndpoint: "/api/pusher",
        },
      );

      const channel = pusherInstance.subscribe(`private-${params.subdomain}`);
      setWorkplaceChannel(channel);
      setPusher(pusherInstance);
    }

    return () => {
      workplaceChannel?.unsubscribe();
      pusher?.disconnect();
    };
  }, [params.subdomain, workplaceChannel, pusher]);

  // Debug log for all workplace channel events
  useEffect(() => {
    if (!workplaceChannel) return;

    const logSpecific = (data: unknown) => {
      // eslint-disable-next-line no-console
    };

    workplaceChannel.bind(
      "message.created",
      logSpecific as unknown as (data: unknown) => void,
    );

    // If supported by pusher-js version, also bind all events
    const anyChannel = workplaceChannel as unknown as {
      bind_global?: (cb: (eventName: string, data: unknown) => void) => void;
      unbind_global?: (cb: (eventName: string, data: unknown) => void) => void;
    };

    const logGlobal = (eventName: string, data: unknown) => {
      // eslint-disable-next-line no-console
    };

    if (anyChannel.bind_global) {
      anyChannel.bind_global(logGlobal);
    }

    return () => {
      workplaceChannel.unbind(
        "message.created",
        logSpecific as unknown as (data: unknown) => void,
      );
      if (anyChannel.unbind_global) {
        anyChannel.unbind_global(logGlobal);
      }
    };
  }, [workplaceChannel]);

  const getOrSubscribeChannel = useCallback(
    (name: string): Channel | null => {
      if (!pusher) return null;
      const existing = channelsRef.current.get(name);
      if (existing) {
        existing.refs += 1;
        return existing.channel;
      }
      const ch = pusher.subscribe(name);
      channelsRef.current.set(name, { channel: ch, refs: 1 });
      return ch;
    },
    [pusher],
  );

  const releaseChannel = useCallback(
    (name: string) => {
      const rec = channelsRef.current.get(name);
      if (!pusher || !rec) return;
      rec.refs -= 1;
      if (rec.refs <= 0) {
        try {
          pusher.unsubscribe(name);
        } catch {
          // ignore
        }
        channelsRef.current.delete(name);
      } else {
        channelsRef.current.set(name, rec);
      }
    },
    [pusher],
  );

  const subscribeToEvents = useCallback(
    (subscriptions: ChannelSubscription[]): string | null => {
      if (!subscriptions || subscriptions.length === 0) return null;
      const id = Math.random().toString(36).slice(2);
      const bindings: Array<{
        channelName: string;
        eventName: string;
        handler: ChannelEventHandler;
      }> = [];
      const channelNames: string[] = [];

      for (const spec of subscriptions) {
        const channelName =
          typeof spec.channel === "function"
            ? spec.channel({ subdomain })
            : spec.channel;
        if (!channelName) continue;
        const ch = getOrSubscribeChannel(channelName);
        if (!ch) continue;
        channelNames.push(channelName);
        for (const [eventName, handler] of Object.entries(spec.events)) {
          ch.bind(eventName, handler as unknown as (payload: unknown) => void);
          bindings.push({ channelName, eventName, handler });
        }
      }

      registrationsRef.current.set(id, { channelNames, bindings });
      return id;
    },
    [getOrSubscribeChannel, subdomain],
  );

  const unsubscribeById = useCallback(
    (subscriptionId: string) => {
      const reg = registrationsRef.current.get(subscriptionId);
      if (!reg) return;
      // Unbind all handlers
      for (const { channelName, eventName, handler } of reg.bindings) {
        const rec = channelsRef.current.get(channelName);
        if (rec) {
          rec.channel.unbind(
            eventName,
            handler as unknown as (payload: unknown) => void,
          );
        }
      }
      // Release channel refs
      for (const name of reg.channelNames) {
        releaseChannel(name);
      }
      registrationsRef.current.delete(subscriptionId);
    },
    [releaseChannel],
  );

  return (
    <PusherContext.Provider
      value={{ pusher, workplaceChannel, subscribeToEvents, unsubscribeById }}
    >
      {children}
    </PusherContext.Provider>
  );
};

export default PusherProvider;

// Hooks API
export function useEvents(
  subscriptions: ChannelSubscription[] | null | undefined,
): Record<string, unknown> | null {
  const { subscribeToEvents, unsubscribeById } = usePusher();
  const subIdRef = useRef<string | null>(null);
  const [payloads, setPayloads] = useState<Record<string, unknown>>({});
  const params = useParams<{ subdomain: string }>();
  const subdomain = (params?.subdomain as string | undefined) || null;

  // Memoize a lightweight signature to prevent excessive resubscribes while
  // still responding to meaningful changes (channel names and event keys)
  const signature = useMemo(() => {
    if (!subscriptions) return "";
    const ctx = { subdomain } as { subdomain?: string | null };
    return subscriptions
      .map((s) => {
        const chan =
          typeof s.channel === "function" ? s.channel(ctx) : s.channel;
        const keys = Object.keys(s.events).sort().join(",");
        return `${chan}|${keys}`;
      })
      .join(";");
  }, [subscriptions, subdomain]);

  useEffect(() => {
    if (!subscriptions || subscriptions.length === 0) return;
    // Wrap handlers to capture latest payloads
    const wrapped: ChannelSubscription[] = subscriptions.map((spec) => {
      const wrappedEvents: Record<string, ChannelEventHandler> = {};
      for (const [eventName, handler] of Object.entries(spec.events || {})) {
        wrappedEvents[eventName] = (data: unknown) => {
          try {
            const channelName =
              typeof spec.channel === "function"
                ? spec.channel({ subdomain })
                : spec.channel;
            const key = `${channelName}:${eventName}`;
            setPayloads((prev) => ({ ...prev, [key]: data }));
          } catch {
            // ignore capture errors
          }
          try {
            handler?.(data);
          } catch {
            // ignore handler errors
          }
        };
      }
      return { ...spec, events: wrappedEvents };
    });

    const id = subscribeToEvents(wrapped);
    subIdRef.current = id;
    return () => {
      if (id) unsubscribeById(id);
      subIdRef.current = null;
    };
    // We deliberately do not depend on the handler function identities here to avoid
    // thrashing; callers should memoize handlers if they need stability.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToEvents, unsubscribeById, signature]);

  return Object.keys(payloads).length > 0 ? payloads : null;
}

export function useUnsubscribeFromEvents() {
  const { unsubscribeById } = usePusher();
  return useCallback(
    (subscriptionId: string | null | undefined) => {
      if (!subscriptionId) return;
      unsubscribeById(subscriptionId);
    },
    [unsubscribeById],
  );
}
